import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Loader2, Wallet, TrendingUp, CalendarDays, X, Receipt, AlertTriangle } from 'lucide-react';

import signalApi from '../../../api/signalApi';
import { WeeklySummaryResponse } from '../../../api/types';

interface WeeklySummaryModalProps {
    open: boolean;
    businessId?: string | null;
    onClose: () => void;
    showSignalsAction?: boolean;
    isAdmin?: boolean;
    title?: string;
}

const formatCurrency = (value: number | null | undefined) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    }).format(value || 0);

const formatPercent = (value: number | null | undefined) => `${(value || 0).toFixed(1)}%`;

const formatWeekLabel = (start?: string, end?: string) => {
    if (!start || !end) return 'This week';
    const s = new Date(`${start}T00:00:00`);
    const e = new Date(`${end}T00:00:00`);
    return `Week of ${s.getMonth() + 1}/${s.getDate()}-${e.getMonth() + 1}/${e.getDate()}`;
};

const formatDateLabel = (value?: string | null) => {
    if (!value) return 'No date';
    const dt = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    return Number.isNaN(dt.getTime()) ? String(value) : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatStatusBasis = (value?: string | null) => {
    if (!value) return null;
    if (value === 'operating') return 'Based on operating status';
    if (value === 'qbo') return 'Based on QBO status';
    return `Based on ${value}`;
};

const sectionBadgeClass = (tone: 'amber' | 'rose' | 'sky' | 'emerald') => {
    if (tone === 'amber') return 'bg-amber-50 text-amber-700 ring-1 ring-amber-100';
    if (tone === 'rose') return 'bg-rose-50 text-rose-700 ring-1 ring-rose-100';
    if (tone === 'sky') return 'bg-sky-50 text-sky-700 ring-1 ring-sky-100';
    return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100';
};

const CompactCard = ({
    icon,
    title,
    rows,
    tone = 'neutral',
}: {
    icon: React.ReactNode;
    title: string;
    rows: Array<{ label: string; value: string }>;
    tone?: 'neutral' | 'cash' | 'obligation' | 'margin';
}) => (
    <div
        className={`rounded-2xl border p-5 ${tone === 'cash'
            ? 'border-emerald-200 bg-emerald-50/70'
            : tone === 'obligation'
                ? 'border-amber-200 bg-amber-50/70'
                : tone === 'margin'
                    ? 'border-sky-200 bg-sky-50/70'
                    : 'border-gray-200 bg-gray-50'
            }`}
    >
        <div className="mb-4 flex items-center gap-2 text-gray-900">
            {icon}
            <h3 className="text-sm font-semibold">{title}</h3>
        </div>
        <div className="space-y-2">
            {rows.map((row) => (
                <div key={row.label} className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{row.label}</span>
                    <span className="font-medium text-gray-900">{row.value}</span>
                </div>
            ))}
        </div>
    </div>
);

export default function WeeklySummaryModal({
    open,
    businessId,
    onClose,
    showSignalsAction = true,
    isAdmin = false,
    title = 'Weekly Summary',
}: WeeklySummaryModalProps) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [draft, setDraft] = useState<WeeklySummaryResponse | null>(null);
    const [emailOverride, setEmailOverride] = useState('');
    const isApWeeklySummary = title === 'AP Weekly Summary';
    const apTopActions = draft?.ap_summary?.top_actions || [];

    const { data, isLoading, error } = useQuery({
        queryKey: ['weekly-summary', businessId],
        queryFn: () => signalApi.getWeeklySummary(businessId || undefined),
        enabled: open && !!businessId,
        staleTime: 5 * 60 * 1000,
    });

    useEffect(() => {
        if (open && data) {
            setDraft(data);
        }
    }, [open, data]);

    const isDirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(data), [draft, data]);

    const updateSummaryMutation = useMutation({
        mutationFn: async () => {
            if (!draft?.business_id) throw new Error('Missing business id.');
            return signalApi.updateWeeklySummaryAdmin({
                business_id: draft.business_id,
                last_week_cash: draft.last_week_cash,
                this_week_obligations: draft.this_week_obligations,
                last_week_margin: draft.last_week_margin,
                signal_summary: draft.signal_summary,
            });
        },
        onSuccess: (updated) => {
            setDraft(updated);
            queryClient.setQueryData(['weekly-summary', businessId], updated);
        },
    });

    const sendSummaryMutation = useMutation({
        mutationFn: async () => {
            if (!draft?.business_id) throw new Error('Missing business id.');
            if (isDirty) {
                const updated = await signalApi.updateWeeklySummaryAdmin({
                    business_id: draft.business_id,
                    last_week_cash: draft.last_week_cash,
                    this_week_obligations: draft.this_week_obligations,
                    last_week_margin: draft.last_week_margin,
                    signal_summary: draft.signal_summary,
                });
                setDraft(updated);
                queryClient.setQueryData(['weekly-summary', businessId], updated);
            }
            return signalApi.sendWeeklySummaryEmail(draft.business_id, emailOverride.trim() || undefined);
        },
    });

    const handleNumberChange = (
        section: 'last_week_cash' | 'this_week_obligations' | 'last_week_margin' | 'signal_summary',
        key: string,
        value: string
    ) => {
        const parsed = value === '' ? 0 : Number(value);
        setDraft((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                [section]: {
                    ...prev[section],
                    [key]: Number.isFinite(parsed) ? parsed : 0,
                },
            };
        });
    };

    const handleDriverChange = (key: 'driver' | 'amount', value: string) => {
        setDraft((prev) => {
            if (!prev) return prev;
            const items = [...(prev.this_week_obligations.identified_items || [])];
            const first = items[0] || { driver: '', amount: 0 };
            items[0] = {
                ...first,
                [key]: key === 'amount' ? (Number.isFinite(Number(value)) ? Number(value) : 0) : value,
            };
            return {
                ...prev,
                this_week_obligations: {
                    ...prev.this_week_obligations,
                    identified_items: items,
                },
            };
        });
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 px-4 py-6">
            <div className="relative flex max-h-[calc(100vh-3rem)] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
                <button
                    onClick={onClose}
                    className="absolute right-5 top-5 rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
                    aria-label="Close weekly summary"
                >
                    <X size={20} />
                </button>

                <div className="border-b border-gray-200 px-8 py-7">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                        {title}
                    </div>
                    <h2 className="text-2xl font-semibold text-gray-900">
                        {data ? formatWeekLabel(data.week_start, data.week_end) : title}
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        Quick snapshot of last week and what matters this week.
                    </p>
                </div>

                <div className="overflow-y-auto px-8 py-7">
                    {isLoading && (
                        <div className="flex min-h-[220px] items-center justify-center gap-3 text-sm text-gray-600">
                            <Loader2 size={18} className="animate-spin" />
                            Loading weekly summary...
                        </div>
                    )}

                    {!!error && !isLoading && (
                        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
                            Failed to load weekly summary.
                        </div>
                    )}

                    {draft && !isLoading && !error && (
                        <div className="space-y-6">
                            {isApWeeklySummary ? (
                                <div className="border-b border-amber-100 pb-5">
                                    <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-900">
                                        <Receipt size={16} />
                                        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${sectionBadgeClass('amber')}`}>
                                            AP This Week
                                        </span>
                                    </div>
                                    <div className="grid gap-x-8 gap-y-3 md:grid-cols-2">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-500">Due this week</span>
                                            <span className="font-medium text-gray-900">{formatCurrency(draft.ap_summary?.summary.ap_due_this_week)}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-500">Overdue</span>
                                            <span className="font-medium text-gray-900">{formatCurrency(draft.ap_summary?.summary.ap_overdue_amount)}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-500">Open AP</span>
                                            <span className="font-medium text-gray-900">{formatCurrency(draft.ap_summary?.summary.open_ap_total)}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-500">Projected outflow</span>
                                            <span className="font-medium text-gray-900">{formatCurrency(draft.ap_summary?.summary.projected_outflow_this_week)}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-500">Paid this week</span>
                                            <span className="font-medium text-gray-900">{formatCurrency(draft.ap_summary?.summary.paid_this_week)}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-gray-500">Optional defer</span>
                                            <span className="font-medium text-gray-900">{formatCurrency(draft.ap_summary?.summary.optional_defer_amount)}</span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid gap-4 md:grid-cols-3">
                                    <CompactCard
                                        icon={<Wallet size={18} />}
                                        title="Last Week Cash"
                                        tone="cash"
                                        rows={[
                                            { label: 'Ending balance', value: formatCurrency(draft.last_week_cash.ending_balance) },
                                            { label: 'Cash in', value: formatCurrency(draft.last_week_cash.cash_in) },
                                            { label: 'Cash out', value: formatCurrency(draft.last_week_cash.cash_out) },
                                        ]}
                                    />

                                    <CompactCard
                                        icon={<CalendarDays size={18} />}
                                        title="This Week Obligations"
                                        tone="obligation"
                                        rows={[
                                            { label: 'Projected outflow', value: formatCurrency(draft.this_week_obligations.projected_outflow) },
                                            { label: 'Projected ending balance', value: formatCurrency(draft.this_week_obligations.projected_ending_balance) }
                                        ]}
                                    />

                                    <CompactCard
                                        icon={<TrendingUp size={18} />}
                                        title="Last Week Margin"
                                        tone="margin"
                                        rows={[
                                            { label: 'Revenue', value: formatCurrency(draft.last_week_margin.revenue) },
                                            { label: 'Net profit', value: formatCurrency(draft.last_week_margin.net_profit) },
                                            { label: 'Net margin', value: formatPercent(draft.last_week_margin.net_margin_pct) },
                                        ]}
                                    />

                                </div>
                            )}

                            {isAdmin && (
                                <div className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
                                    <div className="mb-4 text-sm font-semibold text-gray-900">Admin controls</div>
                                    <div className="grid gap-3 md:grid-cols-2">
                                        <label className="text-sm text-gray-700">
                                            <div className="mb-1 text-xs font-medium text-gray-500">Ending balance</div>
                                            <input type="number" className="w-full rounded-md border border-gray-300 px-3 py-2" value={draft.last_week_cash.ending_balance} onChange={(e) => handleNumberChange('last_week_cash', 'ending_balance', e.target.value)} />
                                        </label>
                                        <label className="text-sm text-gray-700">
                                            <div className="mb-1 text-xs font-medium text-gray-500">Cash in</div>
                                            <input type="number" className="w-full rounded-md border border-gray-300 px-3 py-2" value={draft.last_week_cash.cash_in} onChange={(e) => handleNumberChange('last_week_cash', 'cash_in', e.target.value)} />
                                        </label>
                                        <label className="text-sm text-gray-700">
                                            <div className="mb-1 text-xs font-medium text-gray-500">Cash out</div>
                                            <input type="number" className="w-full rounded-md border border-gray-300 px-3 py-2" value={draft.last_week_cash.cash_out} onChange={(e) => handleNumberChange('last_week_cash', 'cash_out', e.target.value)} />
                                        </label>
                                        <label className="text-sm text-gray-700">
                                            <div className="mb-1 text-xs font-medium text-gray-500">Projected outflow</div>
                                            <input type="number" className="w-full rounded-md border border-gray-300 px-3 py-2" value={draft.this_week_obligations.projected_outflow} onChange={(e) => handleNumberChange('this_week_obligations', 'projected_outflow', e.target.value)} />
                                        </label>
                                        <label className="text-sm text-gray-700">
                                            <div className="mb-1 text-xs font-medium text-gray-500">Projected ending balance</div>
                                            <input type="number" className="w-full rounded-md border border-gray-300 px-3 py-2" value={draft.this_week_obligations.projected_ending_balance} onChange={(e) => handleNumberChange('this_week_obligations', 'projected_ending_balance', e.target.value)} />
                                        </label>
                                        <label className="text-sm text-gray-700">
                                            <div className="mb-1 text-xs font-medium text-gray-500">Revenue</div>
                                            <input type="number" className="w-full rounded-md border border-gray-300 px-3 py-2" value={draft.last_week_margin.revenue} onChange={(e) => handleNumberChange('last_week_margin', 'revenue', e.target.value)} />
                                        </label>
                                        <label className="text-sm text-gray-700">
                                            <div className="mb-1 text-xs font-medium text-gray-500">Net profit</div>
                                            <input type="number" className="w-full rounded-md border border-gray-300 px-3 py-2" value={draft.last_week_margin.net_profit} onChange={(e) => handleNumberChange('last_week_margin', 'net_profit', e.target.value)} />
                                        </label>
                                        <label className="text-sm text-gray-700">
                                            <div className="mb-1 text-xs font-medium text-gray-500">Net margin %</div>
                                            <input type="number" className="w-full rounded-md border border-gray-300 px-3 py-2" value={draft.last_week_margin.net_margin_pct} onChange={(e) => handleNumberChange('last_week_margin', 'net_margin_pct', e.target.value)} />
                                        </label>
                                        <label className="text-sm text-gray-700">
                                            <div className="mb-1 text-xs font-medium text-gray-500">Open signals</div>
                                            <input type="number" className="w-full rounded-md border border-gray-300 px-3 py-2" value={draft.signal_summary.open_count} onChange={(e) => handleNumberChange('signal_summary', 'open_count', e.target.value)} />
                                        </label>
                                        <label className="text-sm text-gray-700">
                                            <div className="mb-1 text-xs font-medium text-gray-500">Largest driver</div>
                                            <input type="text" className="w-full rounded-md border border-gray-300 px-3 py-2" value={draft.this_week_obligations.identified_items[0]?.driver || ''} onChange={(e) => handleDriverChange('driver', e.target.value)} />
                                        </label>
                                        <label className="text-sm text-gray-700">
                                            <div className="mb-1 text-xs font-medium text-gray-500">Largest driver amount</div>
                                            <input type="number" className="w-full rounded-md border border-gray-300 px-3 py-2" value={draft.this_week_obligations.identified_items[0]?.amount || 0} onChange={(e) => handleDriverChange('amount', e.target.value)} />
                                        </label>
                                        <label className="text-sm text-gray-700 md:col-span-2">
                                            <div className="mb-1 text-xs font-medium text-gray-500">Optional send-to email</div>
                                            <input type="email" className="w-full rounded-md border border-gray-300 px-3 py-2" placeholder="Leave blank to send to business owner" value={emailOverride} onChange={(e) => setEmailOverride(e.target.value)} />
                                        </label>
                                    </div>
                                    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                                        <button
                                            type="button"
                                            onClick={() => updateSummaryMutation.mutate()}
                                            disabled={updateSummaryMutation.isPending || !isDirty}
                                            className="inline-flex items-center justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            {updateSummaryMutation.isPending ? 'Saving...' : 'Save changes'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => sendSummaryMutation.mutate()}
                                            disabled={sendSummaryMutation.isPending}
                                            className="inline-flex items-center justify-center rounded-md bg-[#5B5DE4] px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            {sendSummaryMutation.isPending ? 'Sending...' : 'Send email'}
                                        </button>
                                    </div>
                                    {(updateSummaryMutation.error || sendSummaryMutation.error) && (
                                        <div className="mt-3 text-sm text-red-600">
                                            {String(updateSummaryMutation.error || sendSummaryMutation.error)}
                                        </div>
                                    )}
                                    {sendSummaryMutation.isSuccess && (
                                        <div className="mt-3 text-sm text-emerald-600">
                                            Weekly summary email sent.
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className={isApWeeklySummary ? 'space-y-6' : 'rounded-2xl border border-gray-200 bg-gradient-to-r from-white to-gray-50 px-5 py-4'}>
                                <div className={isApWeeklySummary ? 'border-t border-rose-100 pt-4' : ''}>
                                    {isApWeeklySummary ? (
                                        <>
                                            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
                                                <AlertTriangle size={16} />
                                                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${sectionBadgeClass('rose')}`}>
                                                    This Week&apos;s AP Actions
                                                </span>
                                            </div>
                                            {apTopActions.length === 0 ? (
                                                <div className="text-sm text-gray-500">No AP actions for this week.</div>
                                            ) : (
                                                <div className="divide-y divide-gray-200">
                                                    {apTopActions.map((action, index) => (
                                                        <div
                                                            key={`${action.action_type || 'action'}-${action.bill_id || action.vendor_id || index}`}
                                                            className="py-3 first:pt-0 last:pb-0"
                                                        >
                                                            <div className="flex items-start justify-between gap-3">
                                                                <div className="min-w-0">
                                                                    <div className="text-sm font-medium text-gray-900">{action.title || 'AP action'}</div>
                                                                    <div className="mt-1 text-sm text-gray-600">{action.description || 'Review this AP item.'}</div>
                                                                </div>
                                                                <div className="shrink-0 text-right">
                                                                    {action.priority ? (
                                                                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                                            P{action.priority}
                                                                        </div>
                                                                    ) : null}
                                                                    {typeof action.amount === 'number' ? (
                                                                        <div className="mt-1 text-sm font-medium text-gray-900">{formatCurrency(action.amount)}</div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <div className="mb-2 text-sm font-semibold text-gray-900">Top actions this week</div>
                                            <div className="space-y-2 text-sm text-gray-600">
                                                <div>
                                                    {draft.signal_summary.due_this_week_count > 0
                                                        ? `${draft.signal_summary.due_this_week_count} signal${draft.signal_summary.due_this_week_count === 1 ? '' : 's'} need review this week.`
                                                        : 'No signals need review this week.'}
                                                </div>
                                                {draft.this_week_obligations.identified_items[0] && (
                                                    <div>
                                                        Largest forecast driver: <span className="font-medium text-gray-900">{draft.this_week_obligations.identified_items[0].driver}</span> ({formatCurrency(draft.this_week_obligations.identified_items[0].amount)})
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>

                                {isApWeeklySummary && (
                                    <>
                                        <div className="border-t border-sky-100 pt-4">
                                            <div className="mb-2 text-sm font-semibold text-gray-900">
                                                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${sectionBadgeClass('sky')}`}>
                                                    Top Unpaid AP
                                                </span>
                                            </div>
                                            {draft.ap_summary?.top_bills?.length ? (
                                                <div className="divide-y divide-gray-200">
                                                    {draft.ap_summary.top_bills.map((bill) => (
                                                        <div
                                                            key={bill.ap_bill_id || `${bill.vendor_name}-${bill.source_id}`}
                                                            className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0 text-sm"
                                                        >
                                                            <div className="min-w-0">
                                                                <div className="truncate font-medium text-gray-900">{bill.vendor_name || 'Vendor'}</div>
                                                                <div className="text-xs text-gray-500">
                                                                    {bill.item_label || 'Bill'} · {bill.accounting_category || 'Unclassified'} · {bill.accounting_account_name || 'No account'} · {bill.status || 'open'} · {formatDateLabel(bill.due_date)}
                                                                </div>
                                                            </div>
                                                            <div className="shrink-0 font-medium text-gray-900">{formatCurrency(bill.open_amount)}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-sm text-gray-500">No unpaid AP items highlighted this week.</div>
                                            )}
                                        </div>

                                        <div className="border-t border-emerald-100 pt-4">
                                            <div className="mb-2 text-sm font-semibold text-gray-900">
                                                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${sectionBadgeClass('emerald')}`}>
                                                    Largest Cash Drivers
                                                </span>
                                            </div>
                                            <div className="space-y-2 text-sm text-gray-600">
                                                {draft.this_week_obligations.identified_items[0] && (
                                                    <div className="flex items-start justify-between gap-3">
                                                        <span className="text-gray-500">Forecast driver</span>
                                                        <span className="text-right font-medium text-gray-900">
                                                            {draft.this_week_obligations.identified_items[0].driver} ({formatCurrency(draft.this_week_obligations.identified_items[0].amount)})
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="flex items-start justify-between gap-3">
                                                    <span className="text-gray-500">AP due + overdue</span>
                                                    <span className="text-right font-medium text-gray-900">
                                                        {formatCurrency((draft.ap_summary?.summary.ap_due_this_week || 0) + (draft.ap_summary?.summary.ap_overdue_amount || 0))}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="flex items-center justify-between border-t border-gray-200 pt-5">
                                <div className="text-xs text-gray-500">
                                    Generated {new Date(draft.generated_at).toLocaleString()}
                                </div>
                                {showSignalsAction && (
                                    <button
                                        onClick={() => {
                                            onClose();
                                            navigate('/signals');
                                        }}
                                        className="inline-flex items-center gap-2 rounded-md bg-[#5B5DE4] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5B5DE4]/80"
                                    >
                                        See actions
                                        <ArrowRight size={16} />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
